import { computed, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { PERMISSIONS } from '../Enums/permission.enum';
import { IntuneDevice } from '../Models/Intune/intune-device.model';
import { IntuneUser } from '../Models/Intune/intune-user.model';
import { AuthService } from '../Modules/Shared/Services/auth.service';
import { ResourceStateService } from '../Modules/Shared/Services/resource-state.service';
import { TranslationService } from '../Modules/Shared/Services/translation.service';
import { DevicesService } from '../Services/Intune/devices.service';
import { UsersService } from '../Services/Intune/users.service';
import { IndexResponse as DevicesIndexResponse } from '../Interfaces/api/Intune/devices.interface';
import { IndexResponse as UsersIndexResponse } from '../Interfaces/api/Intune/users.interface';
import { HOME_DESTINATIONS } from '../Modules/Workspace/Consts/home-destinations.const';
import {
  HOME_SPOTLIGHT_DEBOUNCE_MS,
  HOME_SPOTLIGHT_DESTINATION_LIMIT,
  HOME_SPOTLIGHT_DIRECTORY_LIMIT,
  HOME_SPOTLIGHT_MIN_QUERY_LENGTH,
} from '../Modules/Workspace/Consts/home.const';
import {
  HOME_SPOTLIGHT_DEVICE_FIELDS,
  HOME_SPOTLIGHT_USER_FIELDS,
} from '../Modules/Workspace/Consts/home-spotlight-fields.const';
import { HomeDestination } from '../Modules/Workspace/Interfaces/home-destination.interface';
import { isHomeDestinationVisible } from '../Modules/Workspace/Utils/home-destination.utils';
import { HomeSpotlightEntry } from '../Modules/Workspace/Interfaces/home-spotlight-entry.interface';
import { HomeSpotlightGroup } from '../Modules/Workspace/Interfaces/home-spotlight-group.interface';
import { HomeSpotlightMatch } from '../Modules/Workspace/Interfaces/home-spotlight-match.interface';

@Injectable()
export class HomeSpotlightFacade {
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _translationService = inject(TranslationService);
  private readonly _devicesService = inject(DevicesService);
  private readonly _usersService = inject(UsersService);
  private readonly _authService = inject(AuthService);

  private readonly _user = toSignal(this._authService.currentUser$, { initialValue: null });

  private readonly _canSearchDevices = computed(() => {
    const user = this._user();
    return (
      (user?.hasPermission(PERMISSIONS.DEVICE_READ) ?? false) &&
      (user?.hasEntitlement('device_management') ?? false)
    );
  });

  private readonly _canSearchUsers = computed(() => {
    const user = this._user();
    return (
      (user?.hasPermission(PERMISSIONS.INTUNE_USER_READ) ?? false) &&
      (user?.hasEntitlement('user_management') ?? false)
    );
  });

  private readonly _query = signal('');

  private readonly _debouncedQuery = toSignal(
    toObservable(this._query).pipe(
      map(term => term.trim()),
      debounceTime(HOME_SPOTLIGHT_DEBOUNCE_MS),
      distinctUntilChanged()
    ),
    { initialValue: '' }
  );

  private readonly _remoteQuery = computed(() => {
    const term = this._debouncedQuery();
    return term.length >= HOME_SPOTLIGHT_MIN_QUERY_LENGTH ? term : null;
  });

  private readonly _devicesResource = httpResource<DevicesIndexResponse>(() => {
    const term = this._remoteQuery();
    if (term === null || !this._canSearchDevices()) return undefined;
    return this._devicesService.index({
      search: term,
      top: HOME_SPOTLIGHT_DIRECTORY_LIMIT,
      select: [...HOME_SPOTLIGHT_DEVICE_FIELDS],
    });
  });

  private readonly _usersResource = httpResource<UsersIndexResponse>(() => {
    const term = this._remoteQuery();
    if (term === null || !this._canSearchUsers()) return undefined;
    return this._usersService.index({
      search: term,
      top: HOME_SPOTLIGHT_DIRECTORY_LIMIT,
      select: [...HOME_SPOTLIGHT_USER_FIELDS],
    });
  });

  readonly query = this._query.asReadonly();

  readonly hasQuery = computed(() => this._query().trim().length > 0);

  readonly canSearchDirectory = computed(() => this._canSearchDevices() || this._canSearchUsers());

  readonly isTyping = computed(() => this.hasQuery() && this._query().trim() !== this._debouncedQuery());

  readonly isSearching = computed(
    () =>
      this._remoteQuery() !== null &&
      (this._devicesResource.isLoading() || this._usersResource.isLoading())
  );

  readonly groups = computed<HomeSpotlightGroup[]>(() => this.buildGroups());

  readonly entries = computed<HomeSpotlightEntry[]>(() =>
    this.groups().flatMap(group => group.entries)
  );

  readonly isEmpty = computed(
    () => this.hasQuery() && !this.isTyping() && !this.isSearching() && this.entries().length === 0
  );

  readonly suggestions = computed<HomeSpotlightGroup[]>(() => this.buildSuggestions());

  private readonly _localizedDestinations = computed<HomeSpotlightMatch[]>(() =>
    this.localizeDestinations(this._translationService.getLanguage())
  );

  setQuery(term: string): void {
    this._query.set(term);
  }

  clear(): void {
    this._query.set('');
  }

  private buildSuggestions(): HomeSpotlightGroup[] {
    const entries = this._localizedDestinations()
      .slice(0, HOME_SPOTLIGHT_DESTINATION_LIMIT)
      .map(match => match.entry);

    if (entries.length === 0) return [];

    return [{ kind: 'destination', titleKey: 'app.home.spotlight.groups.suggestions', entries }];
  }

  private localizeDestinations(language: string): HomeSpotlightMatch[] {
    return this.visibleDestinations().map(destination => {
      const entry = this.toDestinationEntry(destination);
      return { entry, searchText: `${entry.label} ${entry.hint}`.toLocaleLowerCase(language) };
    });
  }

  private buildGroups(): HomeSpotlightGroup[] {
    const language = this._translationService.getLanguage();
    const needle = this._query().trim().toLocaleLowerCase(language);
    if (needle.length === 0) return [];

    const groups: HomeSpotlightGroup[] = [];
    const destinations = this.matchDestinations(needle);

    if (destinations.length > 0) {
      groups.push({
        kind: 'destination',
        titleKey: 'app.home.spotlight.groups.destinations',
        entries: destinations,
      });
    }

    if (this.isTyping() || this.isSearching()) return groups;

    const devices = this.mapDevices();
    if (devices.length > 0) {
      groups.push({ kind: 'device', titleKey: 'app.home.spotlight.groups.devices', entries: devices });
    }

    const users = this.mapUsers();
    if (users.length > 0) {
      groups.push({ kind: 'user', titleKey: 'app.home.spotlight.groups.users', entries: users });
    }

    return groups;
  }

  private matchDestinations(needle: string): HomeSpotlightEntry[] {
    return this._localizedDestinations()
      .filter(match => match.searchText.includes(needle))
      .slice(0, HOME_SPOTLIGHT_DESTINATION_LIMIT)
      .map(match => match.entry);
  }

  private visibleDestinations(): HomeDestination[] {
    return HOME_DESTINATIONS.filter(destination => isHomeDestinationVisible(destination, this._user()));
  }

  private toDestinationEntry(destination: HomeDestination): HomeSpotlightEntry {
    return {
      key: `destination:${destination.key}`,
      kind: 'destination',
      icon: destination.icon,
      label: this._translationService.instant(destination.labelKey),
      hint: this._translationService.instant(destination.descriptionKey),
      routerLink: destination.routerLink,
    };
  }

  private mapDevices(): HomeSpotlightEntry[] {
    const rows = this._resourceState.valueOf(this._devicesResource)?.data.value ?? [];
    return rows
      .map(row => new IntuneDevice(row))
      .filter(device => device.routeId().length > 0)
      .map(device => ({
        key: `device:${device.routeId()}`,
        kind: 'device' as const,
        icon: 'devices',
        label: device.deviceName ?? device.managedDeviceName ?? '',
        hint: device.userPrincipalName ?? device.userDisplayName ?? '',
        routerLink: ['/devices', device.routeId(), 'overview'],
      }));
  }

  private mapUsers(): HomeSpotlightEntry[] {
    const rows = this._resourceState.valueOf(this._usersResource)?.data.value ?? [];
    return rows
      .map(row => new IntuneUser(row))
      .filter(user => user.routeId().length > 0)
      .map(user => ({
        key: `user:${user.routeId()}`,
        kind: 'user' as const,
        icon: 'person',
        label: user.displayName ?? user.userPrincipalName ?? '',
        hint: user.userPrincipalName ?? user.jobTitle ?? '',
        routerLink: ['/users', user.routeId(), 'overview'],
      }));
  }
}
